import { useState } from "react";
import { Plus, Maximize2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Panel, Empty } from "@/components/crm/common";
import { FormDialog } from "@/components/crm/FormDialog";
import { WhatsAppChat } from "@/components/crm/chat/WhatsAppChat";
import { useAuth } from "@/context/AuthContext";

function flatten(team) {
  const out = [];
  const stack = team.filter((t) => !t.reports_to).map((t) => [t, 0]).reverse();
  while (stack.length) {
    const [t, d] = stack.pop();
    out.push([t, d]);
    team.filter((k) => k.reports_to === t.name).reverse().forEach((k) => stack.push([k, d + 1]));
  }
  return out;
}

function Tree({ team, onEdit }) {
  return (
    <div>
      {flatten(team).map(([t, d]) => (
        <button key={t.id} data-testid={`team-node-${t.id}`} onClick={() => onEdit(t)} className="flex w-full items-center gap-2 rounded-lg py-1.5 text-left hover:bg-zinc-50" style={{ paddingLeft: d * 18 }}>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">{t.name[0]}</span>
          <div className="min-w-0"><div className="truncate text-sm font-semibold text-zinc-800">{t.name}</div><div className="truncate text-[11px] text-zinc-500">{t.title} · {t.department}</div></div>
        </button>
      ))}
    </div>
  );
}

export default function TeamHub() {
  const { can } = useAuth();
  const team = useCollection("team");
  const msgs = useCollection("messages");
  const [member, setMember] = useState(null);
  const escalations = msgs.items.filter((m) => m.kind === "escalation");

  return (
    <>
      <PageHeader num="09" title="Team Hub" subtitle="Reporting structure, project communication and daily updates.">
        <Button asChild data-testid="team-open-chat-fullscreen"><Link to="/chat"><Maximize2 className="h-4 w-4 mr-1" />Open chat full screen</Link></Button>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-[250px_minmax(0,1fr)_280px]">
        <Panel title="Reporting hierarchy" action={can("Team", "create") && <Button data-testid="add-team-member-button" size="sm" variant="ghost" onClick={() => setMember({})}><Plus className="h-3.5 w-3.5" /></Button>}>
          <Tree team={team.items} onEdit={(t) => can("Team", "edit") && setMember(t)} />
          <p className="text-[11px] text-zinc-400 mt-4">Click a person to edit contact details. Conceptual preview — detailed relationships follow the complete SOP.</p>
        </Panel>
        <div className="min-w-0"><WhatsAppChat /></div>
        <Panel title="Escalations & blockers" testid="escalations-panel">
          {escalations.length ? escalations.map((m) => (
            <div key={m.id} className="border-l-2 border-rose-400 pl-3 py-1.5 mb-3">
              <div className="text-sm text-slate-800">{m.text}</div>
              <div className="text-[11px] text-slate-500">#{m.channel} · {m.author}</div>
            </div>
          )) : <Empty text="No escalations." />}
          <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">11 AM daily project review: agenda auto-built on the Dashboard from open tasks and at-risk projects.</div>
        </Panel>
      </div>
      <FormDialog testid="team-member-form" open={!!member} onOpenChange={(o) => !o && setMember(null)} title={member?.id ? "Edit team member" : "Add team member"} initial={member}
        fields={[{ name: "name", label: "Name", required: true }, { name: "title", label: "Title" }, { name: "department", label: "Department" },
          { name: "reports_to", label: "Reports to", type: "select", options: team.items.filter((t) => t.id !== member?.id).map((t) => t.name) },
          { name: "email", label: "Email", type: "email" }, { name: "phone", label: "Phone" }]}
        onSubmit={async (f) => !!(f.id ? await team.update(f.id, f, "Member updated") : await team.create(f, "Member added"))} />
    </>
  );
}
